import { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PMRWidget from '@zg-rentals/rg-widgets-pmr';

// Actions
import { loadFilterFromUrl } from 'app/shared/flux/actions/FilterActions';
import { handlePMRFilterChange } from 'app/shared/flux/actions/FilterActions';

// Components
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';
import PageHead from './PageHead';
import HeadingCard from 'app/shared/components/HeadingCard';
import AreaLinks from 'app/shared/components/AreaLinks';
import StaticCommonQuestions from 'app/shared/components/StaticCommonQuestions';
import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import MarketSummary from 'app/shared/components/MarketSummary';
import { Anchor } from '@zillow/constellation';

// Constants
import StringConstants from 'app/shared/constants/StringConstants';

// Misc / utils
import { rentalManagerClient } from 'app/shared/utils/api';
import { queryStringToQueryObject } from 'app/shared/utils/urlUtils';
import * as S from './styles';

// Types
import type { PartialFilterType } from 'app/shared/types/Filters';
import type { State } from 'app/shared/types/State';
import { logError } from '@zg-rentals/log-error';

const applyZillowMapControlStyles = () => {
  // @ts-ignore
  import('@zillow/zillow-map-control/dist/ZillowMapControl.less');
};

const PriceMyRentalResultsPage = ({ history }) => {
  const dispatch = useDispatch();
  const address = useSelector((state: State) => state.address);
  const comparables = useSelector((state: State) => state.comparables);
  const filter = useSelector((state: State) => state.filter);
  const app = useSelector((state: State) => state.app);
  const ui = useSelector((state: State) => state.ui);
  const user = useSelector((state: State) => state.user);
  const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = useSelector((state: State) => state.router.location.pathname);

  const { url, areaName, beds, summary } = address?.marketSummary ?? {};
  const {
    medianRent = null,
    monthlyChange = null,
    yearlyChange = null,
    avgDaysOnMarket = null,
    availableRentals = null,
  } = summary ?? {};
  const hasMarketSummary = !!(medianRent || monthlyChange || yearlyChange || avgDaysOnMarket || availableRentals);
  const filterDescription =
    beds === 'All beds'
      ? 'all bedrooms and all property types'
      : `${beds !== '0' ? `${beds} bedroom` : 'studio'} properties`;

  useEffect(() => {
    history.listen((location) => {
      Promise.resolve(dispatch(loadFilterFromUrl(queryStringToQueryObject(location.search)))).catch((err) => {
        logError({
          error: err,
          errorType: 'Filter',
          context: {
            details: 'Results page: Error loading filter from url (CSR)',
            root: 'pages/PriceMyRentalResultsPage#useEffect',
            pathAttempted: pathname,
          },
        });
      });
    });
    setIsMounted(true);
  }, []);

  useEffect(applyZillowMapControlStyles, []);

  const isSmallScreen = isMobile || screenWidth === 'md';

  const setFilter = (partialFilterObj?: PartialFilterType) => dispatch(handlePMRFilterChange(partialFilterObj));

  const MarketSummaryHeading = (
    // @ts-ignore
    <Anchor href={url} target="_blank" fontFamily="serif">
      {areaName} Market Summary
    </Anchor>
  );

  return (
    <>
      <PageHead address={address} canonicalURL={`${StringConstants.ZILLOW_URL}${pathname}`} />
      <S.Container>
        {isMounted ? (
          <S.Content>
            <HeadingCard />
            {hasMarketSummary && (
              <MarketSummary
                heading={MarketSummaryHeading}
                market={areaName}
                filterDescription={filterDescription}
                medianRent={medianRent}
                monthlyChange={monthlyChange}
                yearlyChange={yearlyChange}
                avgDaysOnMarket={avgDaysOnMarket}
                availableRentals={availableRentals}
                isSmallScreen={isSmallScreen}
                cardElevation={0}
                appearance="borderless"
              />
            )}
            <S.Section useMarginTop={hasMarketSummary} usePadding>
              <PMRWidget
                app={{ ...app, googleMapsApiKey: app.googleMapsApiKey.key }}
                ui={ui}
                // @ts-ignore
                address={address}
                comparables={comparables}
                filter={filter}
                // @ts-ignore
                setFilter={setFilter}
                heading={
                  <ResponsiveHeading fontType="h3" level="3" marginBottom="xs">
                    Local rental marketplace
                  </ResponsiveHeading>
                }
                requestClient={rentalManagerClient}
                user={user}
              />
              <S.SectionBorder />
              <AreaLinks />
            </S.Section>
            <UpsellCarouselModule cardElevation={0} appearance="borderless" marginBottom="sm" />
            {/* @ts-ignore */}
            <S.Section usePadding>
              <StaticCommonQuestions />
            </S.Section>
          </S.Content>
        ) : (
          <SpinnerContainer />
        )}
      </S.Container>
    </>
  );
};

export default withRouter(PriceMyRentalResultsPage);
